"use client";

import Image from "next/image";
import Link from "next/link";

import { useTranslation } from "@/lib/hooks/use-translation";
import {
  BlogPost,
  formatBlogDate,
  getLocalizedBlogPost,
} from "../data/blog-posts";
import { blogHref } from "../data/paths";
import { tagPath } from "../data/tags";

type BlogCardProps = {
  post: BlogPost;
  priority?: boolean;
  variant?: "default" | "compact";
};

export function BlogCard({
  post,
  priority = false,
  variant = "default",
}: BlogCardProps) {
  const { language, t } = useTranslation();
  const localizedPost = getLocalizedBlogPost(post, language);
  const isCompact = variant === "compact";

  return (
    // The card is an <article> rather than one big <Link> because the tags at
    // the bottom are now links of their own, and an <a> inside an <a> is
    // invalid — browsers split it and the card link stops working. The
    // post link still covers everything above the tags, so the click target is
    // effectively unchanged; `focus-within` keeps the keyboard ring on the card.
    // No `h-full` here. The card is a grid item, and `height: 100%` on a grid
    // item is a percentage against an auto-sized row — a cyclic dependency the
    // spec resolves to `auto`, while simultaneously disabling the
    // `align-self: stretch` that would have given every card in the row the
    // same height. Setting no height at all is what makes them equal; the grids
    // that hold these cards add `auto-rows-fr` so the rows match each other too.
    <article className="flex flex-col overflow-hidden rounded-2xl bg-white shadow-[0_16px_41.5px_-10px_rgba(0,0,0,0.10)] transition-transform duration-200 hover:-translate-y-2 focus-within:ring-2 focus-within:ring-[#0B64F4] focus-within:ring-offset-4">
      <Link
        href={blogHref(language, `/blog/${localizedPost.slug}`)}
        className="flex flex-1 flex-col outline-none"
      >
      <div className="relative aspect-[16/9] w-full overflow-hidden rounded-b-lg bg-[#F3F4F6]">
        {post.image ? (
          <Image
            src={post.image}
            alt={localizedPost.title}
            fill
            priority={priority}
            sizes={
              isCompact
                ? "(min-width: 1024px) 400px, (min-width: 768px) 50vw, 100vw"
                : "(min-width: 1024px) 411px, (min-width: 768px) 50vw, 100vw"
            }
            className="object-contain"
          />
        ) : null}
      </div>

      <div className={isCompact ? "flex flex-1 flex-col gap-2 p-4" : "flex flex-1 flex-col gap-2 p-4"}>
        <time
          dateTime={post.publishedAt}
          className={isCompact ? "text-xs font-medium leading-5 text-black/60" : "text-sm font-medium leading-5 text-black/60"}
        >
          {formatBlogDate(localizedPost.publishedAt, language)}
        </time>

          <div className="flex flex-1 flex-col gap-2.5">
            <h2
              className={
                isCompact
                  ? "line-clamp-2 text-[15px] font-semibold leading-[23px] text-black"
                  : "line-clamp-2 text-lg font-semibold leading-7 text-black"
              }
            >
              {localizedPost.title}
            </h2>
            <p
              className={
                isCompact
                  ? "line-clamp-2 text-xs leading-5 text-black/60"
                  : "line-clamp-2 text-sm leading-5 text-black/60"
              }
            >
              {localizedPost.excerpt}
            </p>
          </div>
        </div>
      </Link>

      {/* Outside the post link on purpose — see the <article> note above. */}
      <ul className="flex flex-wrap gap-2 px-4 pb-4" aria-label={t("blogPage.tags")}>
        {localizedPost.tags.slice(0, 3).map((tag) => (
          <li key={tag}>
            <Link
              href={blogHref(language, tagPath(tag))}
              className={
                isCompact
                  ? "inline-block rounded-full border border-[#D3D5DB] bg-[#F3F4F6] px-2.5 py-1 text-[10px] font-medium leading-4 text-black/60 transition-colors hover:border-primary/40 hover:text-black/70"
                  : "inline-block rounded-full border border-[#D3D5DB] bg-[#F3F4F6] px-3 py-1 text-[11px] font-medium leading-4 text-black/60 transition-colors hover:border-primary/40 hover:text-black/70"
              }
            >
              {tag}
            </Link>
          </li>
        ))}
      </ul>
    </article>
  );
}
